import { useQuery } from '@tanstack/react-query'
import { apiService } from '../../../shared/services/api'
import { userAccountApi } from '../../../shared/services/userAccountApi'
import type {
  SubscriptionData,
  UsageData,
} from '../components/subscription/SubscriptionInfo'
import type { PlansData } from '../components/subscription/PlanComparison'
import type {
  DashboardStats,
  DashboardUrl,
} from '../../../shared/types/dashboard'

/**
 * 대시보드 데이터 조회.
 *
 * 주기적 폴링 대신 화면 복귀 시 갱신한다. 링크·통계는 사용자가 직접
 * 바꾸는 데이터라 변경 시 쿼리 무효화로 최신화되며,
 * 초 단위 폴링은 불필요한 요청만 만든다.
 */

const STALE_TIME_MS = 60_000
const PLANS_STALE_TIME_MS = 10 * 60_000

export function useDashboardData() {
  const urlsQuery = useQuery({
    queryKey: ['urls'],
    queryFn: () => apiService.getUserUrls({ limit: 10, page: 1 }),
    staleTime: STALE_TIME_MS,
  })

  const statsQuery = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiService.getDashboardStats(),
    staleTime: STALE_TIME_MS,
  })

  const subscriptionQuery = useQuery({
    queryKey: ['user-subscription'],
    queryFn: () => userAccountApi.getSubscription<SubscriptionData>(),
    staleTime: STALE_TIME_MS,
  })

  const usageQuery = useQuery({
    queryKey: ['user-usage'],
    queryFn: () => userAccountApi.getUsage<UsageData>(),
    staleTime: STALE_TIME_MS,
  })

  const availablePlansQuery = useQuery({
    queryKey: ['available-plans'],
    queryFn: () => userAccountApi.getAvailablePlans<PlansData>(),
    staleTime: PLANS_STALE_TIME_MS,
  })

  return {
    urlsQuery,
    statsQuery,
    subscriptionQuery,
    usageQuery,
    availablePlansQuery,
  }
}

/** 응답의 링크 목록을 대시보드 표시용 형태로 정규화 */
export function toDashboardUrls(
  urls: Array<Record<string, unknown>> | undefined
): DashboardUrl[] {
  return (urls ?? []).map(url => ({
    id: String(url.id ?? url._id ?? ''),
    originalUrl: String(url.originalUrl ?? ''),
    shortUrl: String(url.shortUrl ?? ''),
    shortCode: String(url.shortCode ?? ''),
    customCode: url.customCode ? String(url.customCode) : undefined,
    title: url.title ? String(url.title) : undefined,
    clickCount: Number(url.clickCount ?? 0),
    createdAt: String(url.createdAt ?? new Date().toISOString()),
    isActive: url.isActive !== false,
    metadata: url.metadata as DashboardUrl['metadata'],
  }))
}

export default useDashboardData
